import { useMemo } from 'react';
import { FormState } from './useFormState';

export const useFormValidation = (formState: FormState) => {
  return useMemo(() => {
    const { dropdownItems, questionsAnswered, pages } = formState;

    // Calculate dropdown validation
    const dropdownValidation = dropdownItems.reduce(
      (acc, item) => {
        const isRequired = item.options && item.options.length > 0;
        const isSelected = item.selected !== undefined;

        return {
          ...acc,
          total: acc.total + (isRequired ? 1 : 0),
          completed: acc.completed + (isRequired && isSelected ? 1 : 0),
          missing:
            isRequired && !isSelected
              ? [...acc.missing, item.placeholder.DEFAULT_MESSAGE]
              : acc.missing,
        };
      },
      { total: 0, completed: 0, missing: [] as string[] }
    );

    // Calculate question validation
    const answeredKeys = Object.keys(questionsAnswered).filter(
      k => !k.includes('*')
    );
    const questionValidation = answeredKeys.reduce(
      (acc, key) => {
        const answer = questionsAnswered[key];
        const isAnswered = answer?.checkedValue !== undefined;

        return {
          ...acc,
          completed: acc.completed + (isAnswered ? 1 : 0),
          missing: !isAnswered ? [...acc.missing, key] : acc.missing,
        };
      },
      { completed: 0, missing: [] as string[] }
    );

    // Calculate total requirements
    const totalQuestions = pages.reduce(
      (sum, page) => sum + page.points.length,
      0
    );
    const totalRequired = dropdownValidation.total + totalQuestions;
    const totalCompleted =
      dropdownValidation.completed + questionValidation.completed;

    // Determine if form can be saved
    const canSave =
      totalCompleted >= totalRequired &&
      dropdownValidation.missing.length === 0 &&
      questionValidation.missing.length === 0;

    return {
      canSave,
      canSaveDraft: true, // Drafts can always be saved
      progress:
        totalRequired > 0
          ? Math.round((totalCompleted / totalRequired) * 100)
          : 0,
      dropdowns: dropdownValidation,
      questions: questionValidation,
      totalRequired,
      totalCompleted,
      validationErrors: [
        ...dropdownValidation.missing.map(m => `Dropdown required: ${m}`),
        ...questionValidation.missing.map(m => `Question required: ${m}`),
      ],
    };
  }, [formState]);
};
